// src/pages/Print.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import * as XLSX from "xlsx";
import Scanner from "../components/Scanner";
import Loading from "../components/Loading";
import "./Print.css";
import { apiFetch } from "../components/api"

export default function PrintPage() {
  const [type, setType] = useState("product"); // 'product' | 'location' | 'label'
  const [query, setQuery] = useState("");      // 🔎 실시간 검색어
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);   // 전체 제품 바코드 목록
  const [locations, setLocations] = useState([]); // 전체 로케이션 바코드 목록
  const inputRef = useRef(null);

  //label print
  const [printerHealth, setPrinterHealth] = useState("unknown");
  const [healthMessage, setHealthMessage] = useState("");
  const [baseProfile, setBaseProfile] = useState("");
  const [bulkProfile, setBulkProfile] = useState("");
  const [labelText, setLabelText] = useState("");
  const [labelQrEnable, setLabelQrEnable] = useState(true);
  const [labelTextEnable, setLabelTextEnable] = useState(1);
  const [labelFontSize, setLabelFontSize] = useState(22);
  const [labelQrSize, setLabelQrSize] = useState(150);
  const [labelQrVersion, setLabelQrVersion] = useState(0);
  const [labelDirection, setLabelDirection] = useState(1);
  const [labelPreview, setLabelPreview] = useState("");
  const [labelBusy, setLabelBusy] = useState(false);
  const [labelCodeQty, setLabelCodeQty] = useState(1);
  const [labelQty, setLabelQty] = useState(1);

  const fileInputRef = useRef(null);
  const [imageFile, setImageFile] = useState(null);
  const [imageFileName, setImageFileName] = useState("");

  const openImagePicker = () => {
    fileInputRef.current?.click();
  };

  const onChangeImageFile = (e) => {
    setExcelRows([]);
    setExcelFileName("");
    setLabelText("");
    
    const file = e.target.files?.[0] || null;
    setImageFile(file);
    setImageFileName(file ? file.name : "");
  };

  const [excelFileName, setExcelFileName] = useState("");
  const [excelRows, setExcelRows] = useState([]);
  const [excelIdx, setExcelIdx] = useState(0);
  const [excelPreview, setExcelPreview] = useState({ ok: false, dataUrl: "", error: "", text: "", qty: 1});

  const [isMobile, setIsMobile] = useState(false);

  const scannerRef = useRef(null);
  const [isStaff, setIsStaff] = useState(false);

  const hasExcel = (excelRows || []).length > 0;

  const excelTotalSheets = useMemo(() => {
    return (excelRows || []).reduce((acc, r) => {
      const q = Number(r?.qty ?? 1);
      return acc + (Number.isFinite(q) && q > 0 ? q : 1);
    }, 0);
  }, [excelRows]);
  
  // 권한 안내
  // const isAdmin = useMemo(() => {
  //   return localStorage.getItem("username") === "admin";
  // }, []);
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const me = await apiFetch("/account/me/", { method: "GET" });
        if (!alive) return;
        setIsStaff(!!me?.is_staff);
      } catch (e) {
        if (!alive) return;
        setIsStaff(false);
      }
    })();

    return () => { alive = false; };
  }, []);
  const isAdmin = isStaff;

  useEffect(() => {
    const ua = navigator.userAgent || "";
    const uaMobile = /Android|iPhone|iPad|iPod/i.test(ua);

    const check = () => {
      const narrow = window.innerWidth <= 768;  // 폭 768px 이하를 모바일로 간주
      setIsMobile(uaMobile || narrow);
    };

    check(); // 처음 한 번
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    if (!isAdmin && (type === "product" || type === "location")) {
      setType("label");
    }
  }, [isAdmin, type]);

  useEffect(() => {
    const handleFocus = () => {
      if (scannerRef.current) {
        // 혹시 렌더링 타이밍 때문에 늦게 잡히는 경우 대비해 지연 포커스
        setTimeout(() => {
          scannerRef.current.focus();
        }, 100);
      }
    };

    window.addEventListener("focus", handleFocus);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleFocus();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  //print service status
  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setError(null);

      try {
        // 1) 프린터 헬스 체크
        healthCheck();

        // 2) 데이터 로드
        const data1 = await apiFetch("/api/product/barcode/", { method: "GET" });
        if (!alive) return;
        setProducts(data1?.products || []);

        const data2 = await apiFetch("/api/inventory/barcode/", { method: "GET" });
        if (!alive) return;
        setLocations(data2?.locations || []);
      } catch (e) {
        if (!alive) return;
        setError(`바코드 로드 에러 : ${e?.message || "알 수 없는 오류"}`);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    setError("");

    const run = async () => {
      try {
        setLabelBusy(true);

        const j = await apiFetch("/api/print/profile/default/", { method: "GET" });
        if (!j?.ok) throw new Error(j?.error || "프로필 로드 실패");
        if (!alive) return;

        const bp = j.last_profile || "";
        const tp = j.last_bulk_profile || "";

        setBaseProfile(bp);
        setBulkProfile(tp);

        if (type === "label") {
          if (!tp) throw new Error("벌크 프로필이 없습니다.");
          const pj = await apiFetch(`/api/print/profile/${encodeURIComponent(tp)}/`, { method: "GET" });
          if (!pj?.ok) throw new Error(pj?.error || "벌크 프로필 로드 실패");

          const d = pj.data || {};
          if (!alive) return;

          setLabelTextEnable(Number(d.text_enable ?? 1));
          setLabelFontSize(Number(d.font_size ?? 22));
          setLabelQrEnable(Number(d.qr_enable ?? 1));
          setLabelQrSize(Number(d.qr_size ?? 150));
          setLabelQrVersion(Number(d.qr_version ?? 0));
          setLabelDirection(Number(d.direction ?? 1));
          setLabelPreview("");
          setLabelCodeQty(1);
        } else {
          setLabelCodeQty(1);
        }
      } catch (e) {
        if (alive) setError(e?.message || "프로필 데이터 로드 에러");
      } finally {
        if (alive) setLabelBusy(false);
      }
    };

    run();

    return () => {
      alive = false;
    };
  }, [type]);

  useEffect(() => {
    if (type !== "label") return;
    if (!bulkProfile) return;

    const text = labelText.trim();
    if (!text) {
      setLabelPreview("");
    }

    let alive = true;
    const timer = setTimeout(async () => {
      healthCheck();
      try {
        try {
          await apiFetch("/api/print/profile/upsert/", {
            method: "POST",
            body: {
              name: bulkProfile,
              fields: {
                text_enable: labelTextEnable,
                font_size: labelFontSize,
                qr_enable: labelQrEnable,
                qr_size: labelQrSize,
                qr_version: labelQrVersion,
                direction: labelDirection,
              },
            },
          });
        } catch (e) {
          console.error("auto save bulk profile failed", e);
        }

        if (text) {
          const j = await apiFetch("/api/print/label/preview/text/", {
            method: "POST",
            body: {
              profile: bulkProfile,
              data: [text],
            },
          });

          if (!alive) return;
          if (!j.ok) throw new Error(j.error || "미리보기 실패");

          const item = Array.isArray(j.items) ? j.items[0] : null;

          setLabelPreview(item?.dataUrl || "");
        }

        else if (hasExcel) {
          const row = excelRows?.[excelIdx];
          const excelText = (row?.text ?? "").toString().trim();

          if (excelText) {
            setLabelBusy(true);
            try {
              const j2 = await apiFetch("/api/print/label/preview/text/", {
                method: "POST",
                body: {
                  profile: bulkProfile,
                  data: [excelText],
                },
              });

              if (!alive) return;
              if (!j2.ok) throw new Error(j2.error || "미리보기 실패");

              const item = Array.isArray(j2.items) ? j2.items[0] : null;
              setExcelPreview({
                ok: true,
                dataUrl: item?.dataUrl || "",
                error: "",
                text: excelText,
              });

            } finally {
              setLabelBusy(false);
            }
          }
        }
      } catch (e) {
        if (!alive) return;
        setError(`미리보기 로드 에러 : ${e?.message || "알 수 없는 오류"}`);
      }
    }, 200);

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [
    type,
    bulkProfile,
    labelText,
    labelQrEnable,
    labelTextEnable,
    labelFontSize,
    labelQrSize,
    labelQrVersion,
    labelDirection,
  ]);

  useEffect(() => {
    if (type !== "label") return;
    if (!bulkProfile) return;
    if ((excelRows || []).length === 0) return;
    loadExcelPreview(excelIdx, excelRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, bulkProfile, labelQrEnable, excelIdx, excelRows]);

  const healthCheck = async () => {
    try {
      const health = await apiFetch("/api/print/label/health/", { method: "GET" });

      const svc = health.service;

      if (health.ok && svc?.ok) {
        setPrinterHealth("ok");
        setHealthMessage("정상");
      } else {
        setPrinterHealth("fail");
        const errors = [];
        const details = svc?.details;

        if (details) {
          if (!details.font) errors.push("폰트 없음");
          if (!details.logo) errors.push("로고 없음");
          if (!details.printer) errors.push("프린터 없음");
        } else {
          errors.push("서비스 응답 없음");
        }
        setHealthMessage(errors.length > 0 ? errors.join(" / ") : "상태 오류");
      }
    } catch (e) {
      setPrinterHealth("fail");
      setHealthMessage("프린터 서비스 없음");
    }
  };

  // 🔎 실시간 필터 (탭에 따라 표시 목록 변경)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return type === "product" ? products : locations;
    if (type === "product") {
      return products.filter(it =>
        (it.name || "").toLowerCase().includes(q) ||
        (it.barcode || "").toLowerCase().includes(q)
      );
    }
    // location
    return locations.filter(it =>
      (it.container_name || "").toLowerCase().includes(q) ||
      (it.name || "").toLowerCase().includes(q) ||
      (it.display_name || "").toLowerCase().includes(q) ||
      (it.barcode || "").toLowerCase().includes(q)
    );
  }, [query, type, products, locations]);

  // ESC로 검색 초기화
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setQuery("");
      inputRef.current?.focus();
    }
  };

  // 출력 fetch
  const printTextLabels = async ({ profile, data, qty = 1 } = {}) => {
    if (!profile) {
      throw new Error("프로필이 준비되지 않았습니다.");
    }
    if (!data) {
      throw new Error("출력 데이터가 없습니다.");
    }

    const username = localStorage.getItem("username") || "";

    const j = await apiFetch("/api/print/label/text/", {
      method: "POST",
      body: {
        profile,
        data,
        qty,
        username,
      },
    });

    if (!j?.ok) {
      throw new Error(j?.error || "인쇄 API 호출 실패");
    }

    return j;
  };

  // 라벨 출력
  const printLabelNow = async () => {
    try {
      setLabelBusy(true);
      setError("");

      const text = labelText.trim();
      if (!text) {
        throw new Error("출력할 텍스트가 없습니다.");
      }

      await printTextLabels({
        profile: bulkProfile,
        data: [text],
        qty: labelQty,
      });

      alert("✅ 라벨 인쇄 요청을 보냈습니다.");
    } catch (e) {
      setError(`인쇄 실패 : ${e?.message || "알 수 없는 오류"}`);
    } finally {
      setLabelBusy(false);
    }
  };

  // 바코드 출력
  const confirmAndPrint = async (row, t) => {
    if (!isAdmin) {
      alert("관리자만 인쇄할 수 있습니다.");
      return;
    }

    if (!row?.barcode) {
      alert("바코드 데이터가 없습니다.");
      return;
    }

    const label =
      t === "product"
        ? `${row.name || "(이름없음)"} 바코드 ${row.barcode || "-"}`
        : `${row.display_name || row.container_name || "(위치없음)"}${row.name ? `-${row.name}` : ""} 바코드 ${row.barcode || "-"}`;

    if (!window.confirm(`${label}\n출력 하시겠습니까?`)) return;

    setError("");

    try {
      setLabelBusy(true);

      await printTextLabels({
        profile: baseProfile,
        data: [row.barcode],
        qty: labelCodeQty,
      });

      alert("✅ 라벨 인쇄 요청을 보냈습니다.");
    } catch (e) {
      setError(`인쇄 실패: ${e.message || e}`);
    } finally {
      setLabelBusy(false);
    }
  };

  //이미지 파일
  const printFileLabels = async ({ profile, image_path, qty = 1 } = {}) => {
    if (!profile) {
      throw new Error("프로필이 준비되지 않았습니다.");
    }
    if (!image_path) {
      throw new Error("이미지 경로가 없습니다.");
    }

    const username = localStorage.getItem("username") || "";

    const j = await apiFetch("/api/print/label/file/", {
      method: "POST",
      body: {
        profile,
        image_path,
        qty,
        username,
      },
    });

    if (!j?.ok) {
      throw new Error(j?.error || "이미지 인쇄 API 호출 실패");
    }

    return j;
  };

  const printImageNow = async () => {
    try {
      setLabelBusy(true);
      setError("");

      if (!imageFile) {
        throw new Error("출력할 이미지 파일이 없습니다.");
      }

      const fd = new FormData();
      fd.append("file", imageFile);
      fd.append("profile", bulkProfile || "");
      fd.append("qty", String(labelQty));
      fd.append("username", localStorage.getItem("username") || "");

      const j = await apiFetch("/api/print/label/file/", {
        method: "POST",
        body: fd,
      });

      if (!j?.ok) {
        throw new Error(j?.error || "이미지 인쇄 API 호출 실패");
      }

      alert("이미지 라벨 인쇄 요청을 보냈습니다.");
    } catch (e) {
      setError(`이미지 인쇄 실패 : ${e?.message || "알 수 없는 오류"}`);
    } finally {
      setLabelBusy(false);
    }
  };

  const pickQty = (r) => {
    const keys = Object.keys(r || {});
    const map = {};
    keys.forEach((k) => {
      map[String(k).trim().toLowerCase()] = k;
    });

    const cand = ["qty", "수량", "quantity", "count", "cnt"];
    for (const c of cand) {
      const realKey = map[c];
      if (!realKey) continue;

      const raw = (r?.[realKey] ?? "").toString().trim();
      const n = Number(String(raw).replaceAll(",", ""));
      if (Number.isFinite(n) && n > 0) return Math.floor(n);
      return 1;
    }
    return 1;
  };

  // 엑셀 출력
  const confirmBulkPrint = async () => {
    try {
      const list = (excelRows || [])
        .map((row) => {
          const data = (row?.text ?? "").toString().trim();
          if (!data) return null;

          const q = Number(row?.qty ?? 1);
          const qty = Number.isFinite(q) && q > 0 ? Math.floor(q) : 1;
          return { data, qty };
        })
        .filter(Boolean);

      const kinds = list.length;
      const sheets = list.reduce((a, x) => a + (x.qty || 1), 0);

      if (kinds === 0) {
        alert("출력할 데이터가 없습니다.");
        return;
      }

      if (!window.confirm(`총${kinds}종 - ${sheets}장의 라벨이 출력됩니다.\n계속하시겠습니까?`)) return;

      setLabelBusy(true);

      const expanded = list.flatMap((x) => Array.from({ length: x.qty }, () => x.data));

      const j = await printTextLabels({
        profile: bulkProfile,
        data: expanded,
        qty: 1,
      });

      alert(`모든 라벨 인쇄 요청이 완료되었습니다.\n성공: ${j.total} 항목`);
    } catch (e) {
      alert(`일괄 인쇄 중 오류가 발생했습니다.\n${e?.message || e}`);
    } finally {
      setLabelBusy(false);
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setExcelFileName(file.name);
      setError("");
      setLabelBusy(true);

      setLabelText("");
      setLabelPreview("");

      setImageFile(null);
      setImageFileName("");

      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheetName = wb.SheetNames?.[0];
      if (!sheetName) {
        alert("엑셀 시트를 찾을 수 없습니다.");
        return;
      }

      const ws = wb.Sheets[sheetName];
      const jsonRows = XLSX.utils.sheet_to_json(ws, { defval: "" });

      const rows = (jsonRows || [])
        .map((r) => {
          const v = (r.text ?? r.data ?? r.barcode ?? r?.["바코드"] ?? "").toString().trim();
          const fallback = Object.values(r || {})
            .map((x) => (x ?? "").toString().trim())
            .filter(Boolean)
            .join(" ");
          const text = (v || fallback).trim();
          if (!text) return null;

          const qty = pickQty(r);
          return { text, qty };
        })
        .filter(Boolean);

      setExcelRows(rows);
      loadExcelPreview(0, rows);
      alert(`엑셀 업로드 완료: 총 ${rows.length}종 ${rows.reduce((a, x) => a + (x.qty || 1), 0)}장`);
    } catch (err) {
      alert(`엑셀 읽기 실패: ${err?.message || err}`);
    } finally {
      setLabelBusy(false);
      e.target.value = "";
    }
  };

  const loadExcelPreview = async (idx, rowsOverride) => {
    const src = rowsOverride ?? excelRows;
    const list = (src || []).filter((r) => (r?.text ?? "").toString().trim());
    const total = list.length;

    if (total === 0) {
      setExcelPreview({ ok: false, dataUrl: "", error: "데이터 없음", text: ""});
      return;
    }

    let nextIdx = Number(idx);
    if (!Number.isFinite(nextIdx)) nextIdx = 0;
    if (nextIdx < 0) nextIdx = total - 1;
    if (nextIdx >= total) nextIdx = 0;

    setExcelIdx(nextIdx);

    if (!bulkProfile) {
      setExcelPreview({
        ok: false,
        dataUrl: "",
        error: "라벨 프로필 없음",
        text: list[nextIdx].text,
      });
      return;
    }

    try {
      setLabelBusy(true);

      const text = list[nextIdx].text;
      const qty = Number(list[nextIdx]?.qty ?? 1) || 1;

      const j = await apiFetch("/api/print/label/preview/text/", {
        method: "POST",
        body: {
          profile: bulkProfile,
          data: [text],
        },
      });

      if (!j?.ok) throw new Error(j?.error || "미리보기 실패");

      const item = Array.isArray(j.items) ? j.items[0] : null;

      const previewItem = {
        ok: true,
        dataUrl: item?.dataUrl || "",
        error: "",
        text,
        qty,
      };

      setExcelPreview(previewItem);
    } catch (e) {
      const text = list[nextIdx]?.text || "";
      setExcelPreview({
        ok: false,
        dataUrl: "",
        error: e?.message || String(e),
        text,
        qty: Number(list[nextIdx]?.qty ?? 1) || 1,
      });
    } finally {
      setLabelBusy(false);
    }
  };

  useEffect(() => {
    if (type !== "label") return;
    if ((excelRows || []).length === 0) return;

    const onKey = (e) => {
      if (labelBusy) return;

      const tag = (e.target?.tagName || "").toLowerCase();
      const isTyping = tag === "input" || tag === "textarea" || e.target?.isContentEditable;
      if (isTyping) return;

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        loadExcelPreview(excelIdx - 1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        loadExcelPreview(excelIdx + 1);
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [type, excelRows, excelIdx, labelBusy]);

  useEffect(() => {
    if (!imageFile) {
      return;
    }

    let alive = true;

    const run = async () => {
      try {
        setLabelBusy(true);
        setError("");

        const fd = new FormData();
        fd.append("file", imageFile);
        fd.append("profile", bulkProfile || "");

        const j = await apiFetch("/api/print/label/preview/file/", {
          method: "POST",
          body: fd,
        });

        if (!alive) return;

        if (!j?.ok) {
          throw new Error(j?.error || "이미지 미리보기 실패");
        }

        setLabelPreview(j.dataUrl || "");
      } catch (e) {
        if (!alive) return;
        setLabelPreview("");
        setError(e?.message || String(e));
      } finally {
        if (alive) {
          setLabelBusy(false);
        }
      }
    };

    run();

    return () => {
      alive = false;
    };
  }, [imageFile]);

  const handlePrint = async () => {
    healthCheck();
    if (printerHealth === "fail") {
      alert(`인쇄 불가: ${healthMessage}`);
      return;
    }
    if (imageFile) return printImageNow();
    else if (hasExcel) return confirmBulkPrint();
    return printLabelNow();
  };

  return (
    <div className="printns-page">
      <Loading active={loading} />
      <div className="printns-head-row">
        <h1 className="printns-title">바코드 인쇄</h1>
        <div className="printns-status">
          <span
            className={
              "printns-status-dot " +
              (printerHealth === "ok"
                ? "is-ok"
                : printerHealth === "fail"
                ? "is-fail"
                : "is-unknown")
            }
          />
          <span className="printns-status-text">
            {printerHealth === "ok"
              ? (
                <>
                  서비스 정상
                  <div>
                    프로필: <strong>{type === "label" ? (bulkProfile || "없음") : (baseProfile || "없음")}</strong>
                  </div>
                </>
              )
              : printerHealth === "fail"
              ? healthMessage
              : "상태 확인 중"}
          </span>
        </div>
      </div>
      <Scanner ref={scannerRef} onScan={(value) => setQuery(value)} />

      {/* {!isAdmin && (
        <div className="printns-notice">⚠️ admin 사용자만 인쇄할 수 있습니다.</div>
      )} */}

      {/* 탭 + 설정 */}
      <div className="printns-tabs">
        {isAdmin && (
          <>
            <button
              onClick={() => setType("product")}
              className={`printns-btn-tab ${type === "product" ? "is-active" : ""}`}
            >
              제품 바코드
            </button>

            <button
              onClick={() => setType("location")}
              className={`printns-btn-tab ${type === "location" ? "is-active" : ""}`}
            >
              위치 바코드
            </button>
          </>
        )}
        <button
          onClick={() => setType("label")}
          className={`printns-btn-tab ${type === "label" ? "is-active" : ""}`}
        >
          라벨 프린터
        </button>
        <a href="/printerset" className="printns-btn-tab printns-btn-right" title="프린터 설정으로 이동">
          설정
        </a>
      </div>

      {/* 🔎 실시간 검색 입력 */}
      {type !== "label" && (
        <div className="printns-search">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="printns-input"
            placeholder={type === "product" ? "제품명/바코드 검색…" : "랙/번호/표시명/바코드 검색…"}
            autoFocus
          />
          {/* {query && (
            <button className="printns-btn-ghost" onClick={() => { setQuery(""); inputRef.current?.focus(); }}>
              지우기
            </button>
          )} */}
        </div>
      )}

      {/* 표 영역 */}
      <div className="printns-card">
        {loading ? (
          <Loading />
          // <div className="printns-loading">불러오는 중…</div>
        ) : (
          <>
            {type === "product" ? (
              <>
                <div className="printns-grid" style={{ marginBottom: 8 }}>
                  <div className="printns-label">인쇄 장수</div>
                  <input
                    className="printns-input-number"
                    type="number"
                    min={1}
                    max={100}
                    value={labelCodeQty}
                    onChange={(e) => setLabelCodeQty(Math.max(1, Number(e.target.value) || 1))}
                    disabled={labelBusy}
                  />
                </div>
                
                <table className="printns-table">
                  <thead>
                    <tr>
                      <th style={{ width: "40%" }}>제품명</th>
                      <th>바코드</th>
                      {/* <th style={{ width: 50 }}>인쇄</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="printns-empty">결과 없음</td>
                      </tr>
                    ) : (
                      filtered.map(row => (
                        <tr key={row.id}>
                          <td title={row.name || ""}>{row.name || "-"}</td>
                          <td className="printns-mono printns-clickable" 
                            onDoubleClick={() => confirmAndPrint(row, "product")}
                            onClick={(e) => {
                              if (isMobile) confirmAndPrint(row, "product");
                            }}
                          > 
                            {row.barcode || "-"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </>
            ) : type === "location" ? (
              <>
                <div className="printns-grid" style={{ marginBottom: 8 }}>
                  <div className="printns-label">인쇄 장수</div>
                  <input
                    className="printns-input-number"
                    type="number"
                    min={1}
                    max={100}
                    value={labelCodeQty}
                    onChange={(e) => setLabelCodeQty(Math.max(1, Number(e.target.value) || 1))}
                    disabled={labelBusy}
                  />
                </div>
                <table className="printns-table">
                  <thead>
                    <tr>
                      <th style={{ width: "38%" }}>위치(컨테이너)</th>
                      {/* <th>섹션</th> */}
                      <th>바코드</th>
                      {/* <th style={{ width: 50 }}>인쇄</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="printns-empty">결과 없음</td>
                      </tr>
                    ) : (
                      filtered.map(row => (
                        <tr key={row.id}>
                          <td title={row.display_name || ""}>{row.display_name || row.container_name || "-"}</td>
                          {/* <td className="printns-mono">{row.name || "-"}</td> */}
                          <td className="printns-mono printns-clickable" 
                            onDoubleClick={() => confirmAndPrint(row, "location")}
                            onClick={(e) => {
                              if (isMobile) confirmAndPrint(row, "location");
                            }}
                          >
                            {row.barcode || "-"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </>
            ) : (
              <div className="printns-label-panel">
                <div className="printns-card">
                  <div className="printns-quick-title">미리보기</div>

                  {(excelRows || []).length > 0 ? (
                    <div className="printns-excel-nav">
                      <div className="printns-excel-nav-row">
                        <button
                          type="button"
                          className="printns-btn-outline"
                          onClick={() => loadExcelPreview(excelIdx - 1)}
                          disabled={labelBusy || (excelRows || []).length === 0}
                        >
                          ◀
                        </button>

                        <div className="printns-excel-nav-mid">
                          <div className="printns-excel-index">
                            {excelIdx + 1} / {(excelRows || []).length}종
                            {" · "}
                            수량 {(excelRows?.[excelIdx]?.qty ?? 1)}장
                            {/* {" · "}
                            총 {excelTotalSheets}장 */}
                            {labelBusy ? " (불러오는 중...)" : ""}
                          </div>
                          <div className="printns-excel-text" title={excelPreview.text || ""}>
                            {excelPreview.text || "-"}
                          </div>
                        </div>

                        <button
                          type="button"
                          className="printns-btn-outline"
                          onClick={() => loadExcelPreview(excelIdx + 1)}
                          disabled={labelBusy || (excelRows || []).length === 0}
                        >
                          ▶
                        </button>
                      </div>

                      <div className="printns-preview-wrap">
                        {excelPreview.ok && excelPreview.dataUrl ? (
                          <img
                            src={excelPreview.dataUrl}
                            alt="엑셀 라벨 미리보기"
                            className="printns-preview"
                          />
                        ) : (
                          <div className="printns-empty">
                            {excelPreview.error ? `미리보기 실패: ${excelPreview.error}` : "데이터 없음."}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      {labelPreview ? (
                        <div className="printns-preview-wrap">
                          <img
                            src={labelPreview}
                            alt="라벨 미리보기"
                            className="printns-preview"
                          />
                        </div>
                      ) : (
                        <div className="printns-empty">데이터 없음.</div>
                      )}
                    </>
                  )}
                </div>

                <div className="printns-card" style={{ marginBottom: 12 }}>
                  <div className="printns-grid" style={{ marginBottom: 8 }}>
                    <div className="printns-label">라벨 텍스트</div>
                    <textarea
                      className="printns-input"
                      rows={3}
                      value={labelText}
                      onChange={(e) => setLabelText(e.target.value)}
                      disabled={hasExcel || labelBusy}
                      placeholder={
                        (hasExcel || imageFile)
                          ? "엑셀 업로드 또는 이미지 선택 상태에서는 비활성화됩니다."
                          : "라벨에 넣을 내용을 입력하세요 (엔터로 줄바꿈)"
                      }
                    />
                  </div>

                  <div className="printns-grid" style={{ marginBottom: 8 }}>
                    <div className="printns-label">인쇄 장수</div>
                    <input
                      className="printns-input-number"
                      type="number"
                      min={1}
                      max={100}
                      value={labelQty}
                      onChange={(e) => setLabelQty(Math.max(1, Number(e.target.value) || 1))}
                      disabled={labelBusy || hasExcel}
                    />
                  </div>
                  
                  {!imageFile && (
                    <>
                      <div className="printns-grid" style={{ marginBottom: 8 }}>
                        <div className="printns-label">QR 생성</div>
                        <label>
                          <input
                            type="checkbox"
                            checked={!!labelQrEnable}
                            onChange={(e) => setLabelQrEnable(e.target.checked ? 1 : 0)}
                            disabled={labelBusy}
                          />{" "}
                          QR 코드 출력
                        </label>
                      </div>

                      {!!labelQrEnable && (
                        <div className="printns-grid" style={{ marginBottom: 8 }}>
                          <div className="printns-label">하단 텍스트</div>
                          <label>
                            <input
                              type="checkbox"
                              checked={!!labelTextEnable}
                              onChange={(e) => setLabelTextEnable(e.target.checked ? 1 : 0)}
                              disabled={labelBusy}
                            />{" "}
                            QR 아래 텍스트 표시
                          </label>
                        </div>
                      )}

                      <div className="printns-grid" style={{ marginBottom: 8 }}>
                        <div className="printns-label">폰트 크기(px)</div>
                        <input
                          className="printns-input-number"
                          type="number"
                          min={6}
                          max={80}
                          value={labelFontSize}
                          onChange={(e) => setLabelFontSize(Number(e.target.value) || 0)}
                          disabled={labelBusy}
                        />
                      </div>

                      <div className="printns-grid" style={{ marginBottom: 8 }}>
                        <div className="printns-label">방향 (1=정방향)</div>
                        <select
                          className="printns-input-number"
                          value={labelDirection}
                          onChange={(e) => setLabelDirection(Number(e.target.value) || 0)}
                          disabled={labelBusy}
                        >
                          <option value={0}>0 (180° 회전)</option>
                          <option value={1}>1 (정방향)</option>
                          <option value={2}>2 (90° 회전)</option>
                          {/* <option value={3}>3 (270° 회전)</option> */}
                        </select>
                      </div>

                      {!!labelQrEnable && (
                        <>
                          <div className="printns-grid" style={{ marginBottom: 8 }}>
                            <div className="printns-label">QR 크기(px)</div>
                            <input
                              className="printns-input-number"
                              type="number"
                              min={16}
                              max={600}
                              value={labelQrSize}
                              onChange={(e) => setLabelQrSize(Number(e.target.value) || 0)}
                              disabled={labelBusy}
                            />
                          </div>
                        
                          <div className="printns-grid" style={{ marginBottom: 8 }}>
                            <div className="printns-label">QR 버전 (0=자동, 1~40)</div>
                            <input
                              className="printns-input-number"
                              type="number"
                              min={0}
                              max={40}
                              value={labelQrVersion}
                              onChange={(e) => {
                                const v = Number(e.target.value);
                                if (Number.isNaN(v)) {
                                  setLabelQrVersion(0);
                                } else {
                                  // 범위 클램프
                                  const clamped = Math.max(0, Math.min(40, v));
                                  setLabelQrVersion(clamped);
                                }
                              }}
                              disabled={labelBusy}
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>

                <div className="printns-card" style={{ marginBottom: 12 }}>
                  <div className="printns-quick-title">엑셀 데이터 / 이미지 파일 출력</div>

                  <div className="printns-grid" style={{ marginBottom: 8 }}>
                    <div className="printns-label">파일</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                      <input
                        id="excel-upload"
                        className="printns-file-input"
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleExcelUpload}
                        disabled={labelBusy}
                      />

                      <label
                        className={`printns-btn-tab ${labelBusy ? "is-busy" : ""}`}
                        htmlFor="excel-upload"
                        style={{ whiteSpace: "nowrap" }}
                      >
                        파일 선택
                      </label>

                      <span className="printns-file-name" style={{ minWidth: 0 }}>
                        {excelFileName || "선택된 파일 없음"}
                      </span>
                    </div>
                  </div>

                  <div className="printns-grid" style={{ marginBottom: 8 }}>
                    <div className="printns-label">이미지 파일</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input
                        className="printns-input"
                        value={imageFileName}
                        readOnly
                        placeholder="이미지 파일을 선택하십시오."
                        onClick={openImagePicker}
                      />
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={onChangeImageFile}
                      />
                    </div>
                  </div>

                  <div className="printns-grid" style={{ marginBottom: 8 }}>
                    <div className="printns-label">출력 개수</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <button
                        type="button"
                        className="printns-btn-tab"
                        onClick={() => {
                          setExcelRows([]);
                          setExcelFileName("");
                          setImageFile(null);
                          setImageFileName("");
                          setLabelPreview("");
                        }}
                        disabled={
                          labelBusy &&
                          (excelRows || []).length === 0 &&
                          !imageFile
                        }
                        style={{ whiteSpace: "nowrap" }}
                      >
                        비우기
                      </button>

                      <span className="printns-file-name" style={{ whiteSpace: "nowrap" }}>
                        {imageFile
                          ? `이미지 총1종 - ${labelQty}장`
                          : `엑셀 총${(excelRows || []).length}종 - ${excelTotalSheets}장`}
                      </span>
                    </div>
                  </div>

                </div>
                  <button
                    className={`printns-btn-primary ${(labelBusy) ? "is-busy" : ""}`}
                    onClick={handlePrint}
                    disabled={
                      labelBusy ||
                      (!imageFile && !hasExcel && !labelText.trim())
                    }
                  >
                    라벨 인쇄
                  </button>
                </div>
            )}
          </>
        )}
        {error && (
          <div className="printns-error">{error}</div>
        )}
      </div>
    </div>
  );
}
